import React, { useState } from 'react';
import { TaskFilter, ExtendedTaskFilter, PRIORITIES, STATUSES } from '../types/Task';
import { Filter, X, Search, Calendar, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import './FilterBar.css';

interface FilterBarProps {
  filter: ExtendedTaskFilter;
  onFilterChange: (filter: ExtendedTaskFilter) => void;
  availableOwners?: string[];
  availableReporters?: string[];
  availableLabels?: string[];
  availableStatuses?: string[];
  filteredCount?: number;
  totalCount?: number;
}

const FilterBar: React.FC<FilterBarProps> = ({ 
  filter, 
  onFilterChange, 
  availableOwners = [],
  availableReporters = [],
  availableLabels = [],
  availableStatuses = [],
  filteredCount,
  totalCount
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleFilterChange = (key: keyof ExtendedTaskFilter, value: any) => {
    const newFilter = { ...filter };
    if (value === '' || value === null || value === undefined || 
        (Array.isArray(value) && value.length === 0)) {
      delete newFilter[key];
    } else {
      newFilter[key] = value;
    }
    onFilterChange(newFilter);
  };

  const handleMultiSelectChange = (key: 'priorities' | 'labels', value: string) => {
    const currentValues = filter[key] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    handleFilterChange(key, newValues.length > 0 ? newValues : undefined);
  };

  const clearAllFilters = () => {
    onFilterChange({});
  };

  const hasActiveFilters = Object.keys(filter).length > 0;
  const hasBasicFilters = filter.search || filter.status || filter.priority || filter.owner || filter.tag || filter.label;
  const hasAdvancedFilters = filter.reportedBy || filter.createdDateFrom || filter.createdDateTo || 
    filter.expectedDateFrom || filter.expectedDateTo || filter.reportedDateFrom || filter.reportedDateTo ||
    filter.originalEstimateMin || filter.originalEstimateMax || filter.actualTimeMin || filter.actualTimeMax ||
    filter.isOverdue || filter.isDueToday || filter.isDueThisWeek || filter.hasVideoTutorial ||
    (filter.priorities && filter.priorities.length > 0) || (filter.labels && filter.labels.length > 0);

  const getQuickFilterCount = () => {
    let count = 0;
    if (filter.isOverdue) count++;
    if (filter.isDueToday) count++;
    if (filter.isDueThisWeek) count++;
    if (filter.hasVideoTutorial) count++;
    return count;
  };

  return (
    <div className="filter-bar">
      <div className="filter-header">
        <div className="filter-title">
          <Filter size={16} />
          <span>filters & search</span>
          {hasActiveFilters && (
            <span className="filter-count">({Object.keys(filter).length})</span>
          )}
          {filteredCount !== undefined && totalCount !== undefined && (
            <span className="task-count">
              {hasActiveFilters && filteredCount !== totalCount ? (
                <>{filteredCount} of {totalCount} tasks</>
              ) : (
                <>{totalCount} tasks</>
              )}
            </span>
          )}
        </div>
        <div className="filter-header-actions">
          <button 
            className="expand-filters-btn"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            <span>{isExpanded ? 'collapse' : 'expand'}</span>
          </button>
          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearAllFilters}>
              <X size={14} />
              <span>clear all</span>
            </button>
          )}
        </div>
      </div>

      {/* Global Search */}
      <div className="search-section">
        <div className="search-input-wrapper">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search tasks by summary, description, or any content..."
            value={filter.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="global-search-input"
          />
          {filter.search && (
            <button 
              className="clear-search-btn"
              onClick={() => handleFilterChange('search', '')}
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Quick Filters */}
      <div className="quick-filters">
        <div className="quick-filter-group">
          <span className="quick-filter-label">quick filters:</span>
          <div className="quick-filter-buttons">
            <button
              className={`quick-filter-btn ${filter.isOverdue ? 'active' : ''}`}
              onClick={() => handleFilterChange('isOverdue', !filter.isOverdue ? true : undefined)}
            >
              <Clock size={14} />
              overdue
            </button>
            <button
              className={`quick-filter-btn ${filter.isDueToday ? 'active' : ''}`}
              onClick={() => handleFilterChange('isDueToday', !filter.isDueToday ? true : undefined)}
            >
              <Calendar size={14} />
              due today
            </button>
            <button
              className={`quick-filter-btn ${filter.isDueThisWeek ? 'active' : ''}`}
              onClick={() => handleFilterChange('isDueThisWeek', !filter.isDueThisWeek ? true : undefined)}
            >
              <Calendar size={14} />
              due this week
            </button>
            <button
              className={`quick-filter-btn ${filter.hasVideoTutorial ? 'active' : ''}`}
              onClick={() => handleFilterChange('hasVideoTutorial', !filter.hasVideoTutorial ? true : undefined)}
            >
              has tutorial
            </button>
          </div>
        </div>
      </div>

      {isExpanded && (
        <>
          {/* Basic Filters */}
          <div className="filter-controls">
            <div className="filter-group">
              <label htmlFor="status-filter">status</label>
              <select
                id="status-filter"
                value={filter.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">all statuses</option>
                {availableStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status.replace('-', ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="priority-filter">priority</label>
              <select
                id="priority-filter"
                value={filter.priority || ''}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
              >
                <option value="">all priorities</option>
                {PRIORITIES.map((priority) => (
                  <option key={priority} value={priority}>
                    {priority}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="owner-filter">owner</label>
              {availableOwners.length > 0 ? (
                <select
                  id="owner-filter"
                  value={filter.owner || ''}
                  onChange={(e) => handleFilterChange('owner', e.target.value)}
                >
                  <option value="">all owners</option>
                  {availableOwners.map((owner) => (
                    <option key={owner} value={owner}>
                      {owner}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id="owner-filter"
                  type="text"
                  placeholder="search by owner..."
                  value={filter.owner || ''}
                  onChange={(e) => handleFilterChange('owner', e.target.value)}
                />
              )}
            </div>

            <div className="filter-group">
              <label htmlFor="tag-filter">tag</label>
              <input
                id="tag-filter"
                type="text"
                placeholder="search by tag..."
                value={filter.tag || ''}
                onChange={(e) => handleFilterChange('tag', e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="label-filter">label</label>
              <input
                id="label-filter"
                type="text"
                placeholder="search by label..."
                value={filter.label || ''}
                onChange={(e) => handleFilterChange('label', e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="sort-filter">sort by</label>
              <div className="sort-controls">
                <select
                  id="sort-filter"
                  value={filter.sortBy || ''}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                >
                  <option value="">default</option>
                  <option value="createdAt">created date</option>
                  <option value="expectedDate">expected date</option>
                  <option value="priority">priority</option>
                  <option value="status">status</option>
                  <option value="summary">title</option>
                </select>
                {filter.sortBy && (
                  <select
                    value={filter.sortOrder || 'desc'}
                    onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                  >
                    <option value="desc">descending</option>
                    <option value="asc">ascending</option>
                  </select>
                )}
              </div>
            </div>
          </div>

          {/* Advanced Filters Toggle */}
          <div className="advanced-toggle">
            <button
              className="advanced-toggle-btn"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              <span>advanced filters</span>
              {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {hasAdvancedFilters && <span className="advanced-indicator">•</span>}
            </button>
          </div>

          {showAdvanced && (
            <div className="advanced-filters">
              {/* Reporter and Date Filters */}
              <div className="filter-controls">
                <div className="filter-group">
                  <label htmlFor="reported-by-filter">reported by</label>
                  {availableReporters.length > 0 ? (
                    <select
                      id="reported-by-filter"
                      value={filter.reportedBy || ''}
                      onChange={(e) => handleFilterChange('reportedBy', e.target.value)}
                    >
                      <option value="">all reporters</option>
                      {availableReporters.map((reporter) => (
                        <option key={reporter} value={reporter}>
                          {reporter}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id="reported-by-filter"
                      type="text"
                      placeholder="search by reporter..."
                      value={filter.reportedBy || ''}
                      onChange={(e) => handleFilterChange('reportedBy', e.target.value)}
                    />
                  )}
                </div>

                <div className="filter-group">
                  <label>created date range</label>
                  <div className="date-range">
                    <input
                      type="date"
                      placeholder="from"
                      value={filter.createdDateFrom || ''}
                      onChange={(e) => handleFilterChange('createdDateFrom', e.target.value)}
                    />
                    <span>to</span>
                    <input
                      type="date"
                      placeholder="to"
                      value={filter.createdDateTo || ''}
                      onChange={(e) => handleFilterChange('createdDateTo', e.target.value)}
                    />
                  </div>
                </div>

                <div className="filter-group">
                  <label>expected date range</label>
                  <div className="date-range">
                    <input
                      type="date"
                      placeholder="from"
                      value={filter.expectedDateFrom || ''}
                      onChange={(e) => handleFilterChange('expectedDateFrom', e.target.value)}
                    />
                    <span>to</span>
                    <input
                      type="date"
                      placeholder="to"
                      value={filter.expectedDateTo || ''}
                      onChange={(e) => handleFilterChange('expectedDateTo', e.target.value)}
                    />
                  </div>
                </div>

                <div className="filter-group">
                  <label>reported date range</label>
                  <div className="date-range">
                    <input
                      type="date"
                      placeholder="from"
                      value={filter.reportedDateFrom || ''}
                      onChange={(e) => handleFilterChange('reportedDateFrom', e.target.value)}
                    />
                    <span>to</span>
                    <input
                      type="date"
                      placeholder="to"
                      value={filter.reportedDateTo || ''}
                      onChange={(e) => handleFilterChange('reportedDateTo', e.target.value)}
                    />
                  </div>
                </div>

                <div className="filter-group">
                  <label>original estimate (hours)</label>
                  <div className="number-range">
                    <input
                      type="number"
                      placeholder="min"
                      min="0"
                      value={filter.originalEstimateMin || ''}
                      onChange={(e) => handleFilterChange('originalEstimateMin', e.target.value ? Number(e.target.value) : undefined)}
                    />
                    <span>to</span>
                    <input
                      type="number"
                      placeholder="max"
                      min="0"
                      value={filter.originalEstimateMax || ''}
                      onChange={(e) => handleFilterChange('originalEstimateMax', e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </div>
                </div>

                <div className="filter-group">
                  <label>actual time (hours)</label>
                  <div className="number-range">
                    <input
                      type="number"
                      placeholder="min"
                      min="0"
                      value={filter.actualTimeMin || ''}
                      onChange={(e) => handleFilterChange('actualTimeMin', e.target.value ? Number(e.target.value) : undefined)}
                    />
                    <span>to</span>
                    <input
                      type="number"
                      placeholder="max"
                      min="0"
                      value={filter.actualTimeMax || ''}
                      onChange={(e) => handleFilterChange('actualTimeMax', e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </div>
                </div>
              </div>

              {/* Multi-select Filters */}
              <div className="multi-select-filters">
                <div className="filter-group">
                  <label>priorities (multiple)</label>
                  <div className="checkbox-group">
                    {PRIORITIES.map((priority) => (
                      <label key={priority} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={(filter.priorities || []).includes(priority)}
                          onChange={() => handleMultiSelectChange('priorities', priority)}
                        />
                        <span className={`priority-indicator priority-${priority}`}></span>
                        {priority}
                      </label>
                    ))}
                  </div>
                </div>

                {availableLabels.length > 0 && (
                  <div className="filter-group">
                    <label>labels (multiple)</label>
                    <div className="checkbox-group">
                      {availableLabels.map((label) => (
                        <label key={label} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={(filter.labels || []).includes(label)}
                            onChange={() => handleMultiSelectChange('labels', label)}
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="active-filters">
          <span className="active-filters-label">active filters:</span>
          <div className="active-filter-tags">
            {Object.entries(filter).map(([key, value]) => {
              if (Array.isArray(value) && value.length > 0) {
                return value.map((item, index) => (
                  <div key={`${key}-${index}`} className="active-filter-tag">
                    <span>{key}: {item}</span>
                    <button
                      onClick={() => handleMultiSelectChange(key as 'priorities' | 'labels', item)}
                      className="remove-filter-btn"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ));
              } else if (value && typeof value !== 'object') {
                return (
                  <div key={key} className="active-filter-tag">
                    <span>
                      {key === 'isOverdue' ? 'overdue' :
                       key === 'isDueToday' ? 'due today' :
                       key === 'isDueThisWeek' ? 'due this week' :
                       key === 'hasVideoTutorial' ? 'has tutorial' :
                       `${key}: ${value}`}
                    </span>
                    <button
                      onClick={() => handleFilterChange(key as keyof ExtendedTaskFilter, '')}
                      className="remove-filter-btn"
                    >
                      <X size={12} />
                    </button>
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterBar; 